import { useCallback, useMemo, useRef, useState } from "react";

import { captureException } from "src/lib/monitoring";

export type PaginatedRowsResult<Row> = {
  rows: Row[];
  totalCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

/**
 * Shared pagination for the asset-picker search hooks. Mirrors
 * `useLibraryImages`: the caller's live query always fetches the first page for
 * the current filters (so a filter change re-fetches with `isLoading` true and
 * the pane skeletons instead of showing stale rows), while "Load more" appends
 * later pages via imperative offset fetches rather than mutating the live
 * query's offset. Deliberately avoids `keepPreviousData` — that would surface
 * the previous filter's rows (and count) while the new fetch is in flight.
 */
export const usePaginatedRows = <Row extends { id: string }>({
  filterKey,
  firstPage,
  totalCount,
  isLoading,
  isFetching,
  fetchPage,
}: {
  /**
   * Primitive key derived from the active filters. Extra pages fetched under a
   * different key never surface, and any in-flight loadMore response fetched
   * under a superseded key is dropped.
   */
  filterKey: string;
  /** The live query's first page for the current filters. */
  firstPage: Row[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  /** Imperatively fetches the page of rows at the given offset. */
  fetchPage: (offset: number) => Promise<Row[]>;
}): PaginatedRowsResult<Row> => {
  // The appended pages are stored with the filter key they were fetched under
  // and *derived* against the current key — not reset via an effect. An
  // effect-based reset runs after paint, so a filter change whose first page
  // is served from the query cache would paint one frame of the previous
  // filter's appended rows before the reset lands.
  const [extra, setExtra] = useState<{ key: string; rows: Row[] }>({
    key: filterKey,
    rows: [],
  });
  const [loadingMoreKey, setLoadingMoreKey] = useState<string | null>(null);

  // Lets an in-flight loadMore see the *current* key when its response lands,
  // so a response fetched under superseded filters is dropped.
  const activeKeyRef = useRef(filterKey);
  activeKeyRef.current = filterKey;

  const isLoadingMore = loadingMoreKey === filterKey;

  const rows = useMemo(() => {
    const extraRows = extra.key === filterKey ? extra.rows : [];
    return extraRows.length > 0 ? [...firstPage, ...extraRows] : firstPage;
  }, [firstPage, extra, filterKey]);
  const hasMore = rows.length < totalCount;

  const loadedCount = rows.length;
  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    const requestKey = activeKeyRef.current;
    setLoadingMoreKey(requestKey);
    try {
      const page = await fetchPage(loadedCount);
      // Drop the response if the filters changed while it was in flight —
      // otherwise stale rows from the previous query would append.
      if (activeKeyRef.current !== requestKey) return;
      // Dedupe by id so an overlapping page can't double-list a row.
      setExtra((prev) => {
        const prevRows = prev.key === requestKey ? prev.rows : [];
        const seen = new Set([
          ...firstPage.map((row) => row.id),
          ...prevRows.map((row) => row.id),
        ]);
        return {
          key: requestKey,
          rows: [...prevRows, ...page.filter((row) => !seen.has(row.id))],
        };
      });
    } catch (err) {
      captureException(err);
    } finally {
      // Only clear the loading flag for the request that's still current; a
      // superseded request must not flip off a newer request's spinner.
      setLoadingMoreKey((prev) => (prev === requestKey ? null : prev));
    }
  }, [fetchPage, isLoadingMore, loadedCount, firstPage]);

  return {
    rows,
    totalCount,
    isLoading,
    isRefreshing: isFetching && !isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  };
};
